import { isEmpty as isEmptyObject } from 'lodash';
import { MEDIUM_CONFIDENCE_THRESHOLD } from './AssistanceField';
import { fieldDataToFieldConfig } from './DynamicField';
import { getMatchingKey, getMatchingSeverity } from './MatchingExplanation.tsx';
import { DEFAULT_VALUE_KEY, FormField, FormFieldConfig, FormFieldData } from './useDocumentConfig';
import { hasFailedValidationCheck } from './validationChecks.ts';

const valueTypeToFieldTypeMap = {
    string: 'StringField',
    integer: 'IntegerField',
    float: 'DecimalField',
    decimal: 'DecimalField',
    boolean: 'BooleanField',
    date: 'DateField',
    datetime: 'DateField',
    dict: 'JSONStringField',
    list: 'ListField',
};

export const fieldGroupSubfieldNames = {
    AddressField: [
        'addressId',
        'addressId2',
        'name',
        'companyName',
        'streetAndNr',
        'postcode',
        'city',
        'country',
        'email',
        'phone',
        'misc',
    ],
    CustomerField: ['customerNumber', 'name', 'address', 'internalNote', 'iln', 'contactPerson', 'customerType'],
    ContactField: ['contactId', 'name', 'email', 'phone'],
};

// the reselect mutation reads a selection that continues on other pages only for these, and for plain string
// subfields such as contact.email (whose data is the whole ContactField)
const ACROSS_PAGES_FIELD_TYPENAMES = ['StringField', 'CustomRagStringExtractionField'];

export const canSelectAcrossPages = (data: FormFieldData, config: FormFieldConfig, valueKey = DEFAULT_VALUE_KEY) => {
    if (valueKey === DEFAULT_VALUE_KEY && !ACROSS_PAGES_FIELD_TYPENAMES.includes(data?.__typename)) return false;
    const { valueType, options, readOnly, disabled } = { ...config, ...fieldDataToFieldConfig(data ?? {}) };
    return valueType === 'string' && !options && !readOnly && !disabled;
};

export const extractText = (client, recordId, bbox, pageIndex, query, documentTypeName, isEmailOcr = false) => {
    return client
        .query({
            query: query,
            variables: {
                recordId,
                bbox,
                pageIndex,
                isEmailOcr,
            },
            fetchPolicy: 'no-cache',
        })
        .then((res) => res?.data?.[documentTypeName + 'ExtractText']?.text || '')
        .catch((err) => {
            console.error(err);
            return '';
        });
};

export const fieldIsGreen = (
    field: Pick<FormField, 'config' | 'value' | 'data'>,
    { includeMatching = true } = {}
): boolean => {
    // TODO: This whole TROX isComposite sucks)
    if (!(field.config?.valueType || field.data?.isComposite)) return false;

    if (
        field.data?.confidenceExplanation?.translationKey === 'explanation.historical_matches' &&
        field.data?.predictionConfidence >= MEDIUM_CONFIDENCE_THRESHOLD
    )
        return true;

    return false;
};

export const fieldIsYellow = (
    field: Pick<FormField, 'config' | 'value' | 'data'>,
    { includeMatching = true } = {}
): boolean => {
    // TODO: This whole TROX isComposite sucks)
    if (!(field.config?.valueType || field.data?.isComposite)) return false;

    const isEmpty = isFieldValueEmpty(field.value);

    // OC Matching (yellow)
    if (includeMatching && field.data?.confidenceExplanation?.explanationDetails?.document_matching_result) {
        const documentMatchingKey = getMatchingKey(
            field.data?.confidenceExplanation?.explanationDetails,
            field.config?.isRequired,
            isEmpty
        );
        const severity = getMatchingSeverity(documentMatchingKey);

        if (severity === 'warning') return true;
    }

    // if optional and empty it's never yellow
    if (!field.config?.isRequired && isEmpty) return false;

    // if it doesn't block automation it's never yellow
    if (field.config?.ignoreConfidence) return false;

    // if required and blocks automation and has value and is below medium confidence
    if (
        !field.config?.ignoreConfidence &&
        !isEmpty &&
        // Note: this logic was changed to have less red fields
        // field.data?.predictionConfidence < MEDIUM_CONFIDENCE_THRESHOLD &&
        // field.data?.predictionConfidence >= LOW_CONFIDENCE_THRESHOLD
        field.data?.predictionConfidence < MEDIUM_CONFIDENCE_THRESHOLD
    )
        return true;

    return false;
};

export const fieldIsRed = (
    field: Pick<FormField, 'config' | 'value' | 'data'>,
    { includeMatching = true } = {}
): boolean => {
    // TODO: This whole TROX isComposite sucks)
    if (!(field.config?.valueType || field.data?.isComposite)) return false;

    const isEmpty = isFieldValueEmpty(field.value);

    // OC Matching (red)
    if (includeMatching && field.data?.confidenceExplanation?.explanationDetails?.document_matching_result) {
        const documentMatchingKey = getMatchingKey(
            field.data?.confidenceExplanation?.explanationDetails,
            field.config?.isRequired,
            isEmpty
        );

        const severity = getMatchingSeverity(documentMatchingKey);

        if (severity === 'error') return true;
    }

    // validation checks
    if (hasFailedValidationCheck(field.data?.validationChecks)) return true;

    // if required and is empty
    if (field.config?.isRequired && isEmpty) return true;

    // if optional and empty it's never red
    if (!field.config?.isRequired && isEmpty) return false;

    // if required and blocks automation and has value and is below low confidence
    // Note: this logic was changed to have less red fields
    // if (
    //     field.config?.isRequired &&
    //     !field.config?.ignoreConfidence &&
    //     !isEmpty &&
    //     field.data?.predictionConfidence < LOW_CONFIDENCE_THRESHOLD
    // )
    //     return true;

    return false;
};

export const filterFastTrack = (formFieldGroups: FormField[][]): [FormField[][], { medium: number; low: number }] => {
    let mediumConfidenceFieldsCount = 0;
    let lowConfidenceFieldsCount = 0;

    const isFieldIncomplete = (formField: FormField) => {
        const isFieldYellow = fieldIsYellow(formField);
        const isFieldRed = fieldIsRed(formField);

        if (isFieldYellow || isFieldRed) {
            if (isFieldRed) {
                lowConfidenceFieldsCount++;
            } else if (isFieldYellow) {
                mediumConfidenceFieldsCount++;
            }

            return true;
        }
    };

    return [
        // this way we are first iterating through all items so the counters are correct
        formFieldGroups.filter((formFields) => formFields.map(isFieldIncomplete).some(Boolean)),
        {
            medium: mediumConfidenceFieldsCount,
            low: lowConfidenceFieldsCount,
        },
    ];
};

export const isFieldValueEmpty = (value: any): boolean => {
    // Note "false" is a valid value
    if (typeof value === 'boolean') return false;

    return (
        value === null ||
        value === undefined ||
        value === '' ||
        // no need for deserialization here
        value === '[]' ||
        value === '{}' ||
        // in case we get other objects
        (typeof value === 'object' && isEmptyObject(value))
    );
};

export const checkIsPdfEmbeddedSupported = (): boolean => {
    const hasPdfMimeType = navigator.mimeTypes && navigator.mimeTypes['application/pdf'];

    return !!hasPdfMimeType;
};
